import { useEffect, useState } from "react";
import "../../assests/style/home.css";
import { useDispatch, useSelector } from "react-redux";
import { deleteLink, shortenLinkFree } from "../../redux/features/linkSlice";
import Shorten from "../../components/shorten/Shorten";
import Footer from "../../components/Footer/Footer";
import { motion } from "framer-motion";
import {
  FiLink, FiFolder, FiBarChart2, FiFileText, FiCamera,
  FiCode, FiUser, FiMonitor, FiUsers, FiGlobe,
  FiChrome, FiArrowRight, FiCheck, FiZap, FiShield,
  FiTrendingUp, FiBell, FiUserPlus, FiToggleLeft, FiActivity, FiSettings
} from 'react-icons/fi';
import { SiDiscord, SiSlack } from 'react-icons/si';
import Header from "../../components/Header/Header";
import { useNavigate } from "react-router-dom";

const Home = ({ isDark, toggleDarkMode }) => {
  const { loading, shortenLinksfree } = useSelector((state) => state.shortenLink);
  const [url, setUrl] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate()

  const handleSignupNavigation = () => {
    navigate('/signup')
  }

  //  remove the expired link from localStorage
  useEffect(() => {
    const now = new Date();
    const filteredLinks = shortenLinksfree.filter((link) => {
      const expireDate = new Date(link.expireAt);
      return expireDate > now;
    });
    localStorage.setItem("shortenLinkFree", JSON.stringify(filteredLinks));
  }, []);

  const handleUrlChange = (e) => setUrl(e.target.value);

  const handleSubmitUrl = async (e) => {
    e.preventDefault();
    try {
      const res = await dispatch(shortenLinkFree({ url })).unwrap();
      if (res.status) {
        setUrl("");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteLink = (index) => {
    const updatedLinks = shortenLinksfree.filter((_, i) => i !== index);
    localStorage.setItem("shortenLinkFree", JSON.stringify(updatedLinks));
    dispatch(deleteLink(index));
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const floatAnimation = {
    y: [-10, 10, -10],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Features data
  const coreFeatures = [
    {
      icon: <FiZap className="text-2xl" />,
      title: "Lightning Fast",
      desc: "Generate short links in milliseconds with our optimized infrastructure",
      gradient: "from-yellow-400 to-orange-500"
    },
    {
      icon: <FiShield className="text-2xl" />,
      title: "Enterprise Security",
      desc: "Built from day one with bcrypt password protection — your data deserves serious security",
      gradient: "from-emerald-400 to-teal-500"
    },
    {
      icon: <FiTrendingUp className="text-2xl" />,
      title: "Advanced Analytics",
      desc: "Real-time insights with geographic and demographic data",
      gradient: "from-purple-400 to-pink-500"
    }
  ];

  const allFeatures = [
    {
      icon: <FiLink className="text-2xl text-blue-500" />,
      title: "Smart URL Shortener",
      desc: "Create branded short links with custom domains, expiration dates, and password protection.",
      color: "blue"
    },
    {
      icon: <FiFileText className="text-2xl text-purple-500" />,
      title: "PDF Generator",
      desc: "Convert any webpage to high-quality PDF with custom formatting options.",
      color: "purple"
    },
    {
      icon: <FiCamera className="text-2xl text-amber-500" />,
      title: "Web Screenshots",
      desc: "Capture full-page or element-specific screenshots with mobile and desktop views.",
      color: "amber"
    },
    {
      icon: <FiCode className="text-2xl text-emerald-500" />,
      title: "HTML Scraper",
      desc: "Extract structured data from websites with our intelligent scraping engine.",
      color: "emerald"
    },
    {
      icon: <FiBarChart2 className="text-2xl text-rose-500" />,
      title: "SEO Analyzer",
      desc: "Comprehensive SEO audits with actionable recommendations and competitor analysis.",
      color: "rose"
    },
    {
      icon: <FiUser className="text-2xl text-cyan-500" />,
      title: "Digital Business Cards",
      desc: "Create interactive e-visiting cards with QR codes and contact integration.",
      color: "cyan"
    },
    {
      icon: <FiGlobe className="text-2xl text-teal-500" />,
      title: "Geo Targeting",
      desc: "Control link access by country, region, or city with detailed geographic analytics.",
      color: "teal"
    },
    {
      icon: <FiMonitor className="text-2xl text-violet-500" />,
      title: "Device Targeting",
      desc: "Optimize user experience by targeting specific devices and operating systems.",
      color: "violet"
    },
    {
      icon: <FiChrome className="text-2xl text-sky-500" />,
      title: "Browser Intelligence",
      desc: "Direct users to browser-specific experiences for optimal compatibility.",
      color: "sky"
    },
    {
      icon: <FiUsers className="text-2xl text-fuchsia-500" />,
      title: "Team Collaboration",
      desc: "Invite team members, set permissions, and collaborate on projects seamlessly.",
      color: "fuchsia"
    },
    {
      icon: <FiFolder className="text-2xl text-orange-500" />,
      title: "Smart Organization",
      desc: "Easily organize your links into folders for better management.",
      color: "orange"
    },
    {
      icon: <FiFileText className="text-2xl text-lime-500" />,
      title: "Stack Notes",
      desc: "Keep your team aligned with notes that are easy to create, share, and revisit anytime.",
      color: "lime"
    }
  ];

  // Integrations data
  const integrations = [
    {
      icon: <SiDiscord className="text-3xl" />,
      name: "Discord Integration",
      desc: "Connect Stackly with your Discord server to receive real-time notifications and updates directly in your channels",
      status: "live",
      color: "from-indigo-500 to-purple-600",
      bgColor: "bg-indigo-50 dark:bg-indigo-900/20",
      features: [
        "Instant notifications for team activities",
        "Customizable alert channels",
        "Rich embeds with detailed information",
        "Direct action buttons for quick responses"
      ],
      benefits: [
        "Keep your entire team in sync without leaving Discord",
        "Reduce context switching between platforms",
        "Get critical updates the moment they happen",
      ]
    },
    {
      icon: <SiSlack className="text-3xl" />,
      name: "Slack Integration",
      desc: "Connect Stackly with your Slack workspace to get notifications and updates in your preferred channels",
      status: "live",
      color: "from-green-500 to-teal-600",
      bgColor: "bg-green-50 dark:bg-green-900/20",
      features: [
        "Real-time alerts for team activity",
        "Channel-specific notifications",
        "Customizable notification preferences",
        "Rich message formatting"
      ],
      benefits: [
        "Stay updated without leaving your workflow",
        "Get notified about important link activity",
        "Quick actions directly from Slack messages"
      ]
    },
    // {
    //   icon: <FiSettings className="text-3xl" />,
    //   name: "Custom Notification Preferences",
    //   desc: "Configure exactly which actions trigger notifications for each feature in your workspace",
    //   status: "live",
    //   color: "from-blue-500 to-cyan-600",
    //   bgColor: "bg-blue-50 dark:bg-blue-900/20",
    //   features: [
    //     "Per-feature notification toggles",
    //     "Action-specific preferences (create/update/delete)",
    //     "Team-wide visibility settings",
    //     "Real-time alert delivery"
    //   ]
    // },
  ];

  return (
    <div className={`min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300 ${isDark ? 'dark' : ''}`}>
      <Header isDark={isDark} toggleDarkMode={toggleDarkMode} />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* Animated Background */}
        <div className="absolute inset-0 z-0">
          <motion.div
            className="absolute top-20 left-10 w-72 h-72 bg-blue-500/20 dark:bg-blue-500/10 rounded-full filter blur-[120px]"
            animate={floatAnimation}
          />
          <motion.div
            className="absolute top-40 right-20 w-96 h-96 bg-purple-500/20 dark:bg-purple-500/10 rounded-full filter blur-[120px]"
            animate={{ ...floatAnimation, transition: { ...floatAnimation.transition, delay: 2 } }}
          />
          <motion.div
            className="absolute bottom-20 left-1/3 w-64 h-64 bg-emerald-500/20 dark:bg-emerald-500/10 rounded-full filter blur-[120px]"
            animate={{ ...floatAnimation, transition: { ...floatAnimation.transition, delay: 4 } }}
          />
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

        <motion.div
          className="relative z-10 w-full px-4 sm:px-6 lg:px-8"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <div className="max-w-6xl mx-auto text-center">
            <motion.div variants={itemVariants} className="mt-8">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium mb-8">
                🚀 New: AI-Powered Link Optimization
              </span>
            </motion.div>

            <motion.h1
              className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 text-gray-900 dark:text-white"
              variants={itemVariants}
            >
              <span className="block">Shorten.</span>
              <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent px-2 -mx-2">
                Analyze.
              </span>
              <span className="block">Optimize.</span>
            </motion.h1>

            <h1
              className="text-xl sm:text-2xl text-gray-600 dark:text-white max-w-3xl mx-auto mb-12 leading-relaxed"
            >
              More than just a short link. It's a command center for tracking, managing, and optimizing your digital presence.
            </h1>

            {/* URL Shortener Form */}
            <motion.div className="mb-16 max-w-4xl mx-auto" variants={itemVariants}>
              <form onSubmit={handleSubmitUrl} className="flex flex-col sm:flex-row gap-4 p-2 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700">
                <input
                  type="url"
                  placeholder="✨ Paste your long URL here to create magic..."
                  value={url}
                  required
                  onChange={handleUrlChange}
                  className="flex-grow px-6 py-4 rounded-xl bg-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none text-lg"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-lg whitespace-nowrap flex items-center justify-center gap-2 min-w-[160px]"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <span>Shorten URL</span>
                      <FiArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>

              {/* Results */}
              {shortenLinksfree.length > 0 && (
                <motion.div
                  className="mt-8 space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {shortenLinksfree.map((link, index) => (
                    <Shorten
                      key={index}
                      icon={link.icon || "default_icon.png"}
                      short_URI={link.short_URI}
                      long_URI={link.long_URI}
                      expireAt={link.expireAt}
                      onDelete={() => handleDeleteLink(index)}
                    />
                  ))}
                  <p className="text-center text-gray-600 dark:text-gray-400 text-sm">
                    Free links expire in 24 hours.
                    <span onClick={handleSignupNavigation} className="text-blue-600 dark:text-blue-400 hover:underline font-medium ml-1">
                      Sign up for unlimited links →
                    </span>
                  </p>
                </motion.div>
              )}
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Core Features */}
      <section id="features" className="py-20 bg-gray-50 dark:bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.h2
              className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6"
              variants={itemVariants}
            >
              Why Choose <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Stackly?</span>
            </motion.h2>
            <motion.p
              className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
              variants={itemVariants}
            >
              Built for modern teams who demand speed, security, and insights
            </motion.p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            {coreFeatures.map((feature, index) => (
              <motion.div
                key={index}
                className="group relative bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 hover:-translate-y-2"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`}></div>
                <div className={`w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* All Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.span
              className="inline-block px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium mb-4"
              variants={itemVariants}
            >
              Complete Toolkit
            </motion.span>
            <motion.h2
              className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6"
              variants={itemVariants}
            >
              All-in-One Platform Packed with Powerful Features
            </motion.h2>
            <motion.p
              className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
              variants={itemVariants}
            >
              Everything you need to optimize your digital presence and workflow
            </motion.p>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            {allFeatures.map((feature, index) => (
              <motion.div
                key={index}
                className="group bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600"
                variants={itemVariants}
                whileHover={{ y: -5 }}
              >
                <div className="mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Integrations Section */}
      <section id="integrations" className="py-20 bg-gradient-to-b from-white to-indigo-50/30 dark:from-gray-900 dark:to-indigo-900/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.span
              className="inline-block px-4 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-sm font-medium mb-4"
              variants={itemVariants}
            >
              <FiLink className="inline w-4 h-4 mr-2" />
              Powerful Integrations
            </motion.span>
            <motion.h2
              className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6"
              variants={itemVariants}
            >
              Connect with <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Your Workflow</span>
            </motion.h2>
            <motion.p
              className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
              variants={itemVariants}
            >
              Stackly seamlessly integrates with the tools your team already uses
            </motion.p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            {integrations.map((integration, index) => (
              <motion.div
                key={index}
                className={`group relative ${integration.bgColor} p-8 rounded-2xl border border-gray-200 dark:border-gray-700 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1`}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                {/* Status Badge */}
                <div className="absolute top-4 right-4">
                  {integration.status === 'live' ? (
                    <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      Active
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  )}
                </div>

                {/* Integration Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-r ${integration.color} rounded-2xl flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300`}>
                    {integration.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {integration.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      {index === 0 ? "Real-time alerts in Discord" : index === 1 ? "Team notifications in Slack" : "Notification Settings"}
                    </p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                  {integration.desc}
                </p>

                {/* Features List */}
                <div className="space-y-2 mb-6">
                  {integration.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <FiCheck className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-gray-700 dark:text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Additional Benefits */}
                {(index === 0 || index === 1) && (
                  <div className="mt-6 p-4 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      {index === 0 ? (
                        <SiDiscord className="text-indigo-500" />
                      ) : (
                        <SiSlack className="text-green-500" />
                      )}
                      {index === 0 ? "Discord" : "Slack"} Integration Benefits
                    </h4>
                    <ul className="space-y-2">
                      {integration.benefits.map((benefit, benefitIndex) => (
                        <li key={benefitIndex} className="flex items-start gap-2">
                          <span className={index === 0 ? "text-indigo-500" : "text-green-500"}>•</span>
                          <span className="text-gray-700 dark:text-gray-300 text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Gradient Overlay on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-r ${integration.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`}></div>
              </motion.div>
            ))}
          </motion.div>

          {/* Integration Setup Guides */}
          <motion.div
            className="mt-16 space-y-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Discord Setup Guide */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-8 border border-indigo-200 dark:border-indigo-800">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white">
                    <SiDiscord className="w-12 h-12" />
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    How to Connect Stackly with Discord
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">1. Authorize Stackly</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Click the "Connect Discord Account" button and authorize Stackly to send messages to your server
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">2. Confirm Server</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Confirm Discord server to integrate Stackly
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">3. Select Channel</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Choose which Discord channel should receive Stackly notifications
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">4. Configure Alerts</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Customize which events trigger Discord notifications
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">5. Test Integration</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Send a test notification to verify everything is working
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Slack Setup Guide */}
            <div className="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 rounded-2xl p-8 border border-green-200 dark:border-green-800">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-teal-600 rounded-2xl flex items-center justify-center text-white">
                    <SiSlack className="w-12 h-12" />
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    How to Connect Stackly with Slack
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">1. Connect Slack</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Click the "Connect Slack Account" button and authorize Stackly to send messages
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">2. Select Channel</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Choose which Slack channel should receive Stackly notifications from the channel list
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">3. Join Channel</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Join Stackly with your selected channel to enable notifications
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">4. Test Integration</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Test channel integration by sending a test notification
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Analytics Showcase */}
      <section id="analytics" className="py-20 bg-gradient-to-br overflow-x-hidden from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm font-medium mb-6">
                Advanced Analytics
              </span>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                Insights That Drive <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Results</span>
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
                Get deep insights into your audience behavior with real-time analytics, geographic data, and performance metrics.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  "Real-time click tracking",
                  "Detailed geographic and demographic analytics",
                  "Device, browser, and OS breakdown reports",
                  "Export data in multiple formats (CSV, PDF, JSON)"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <FiCheck className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>

              <button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center gap-2">
                Explore Analytics
                <FiArrowRight className="w-5 h-5" />
              </button>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Analytics Dashboard</h3>
                  <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-medium">
                    Live
                  </span>
                </div>

                {/* Mock Chart */}
                <div className="h-48 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 rounded-xl mb-6 flex items-end justify-center p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-end gap-2 h-full w-full max-w-xs">
                    {[65, 45, 80, 60, 95, 70, 85].map((height, index) => (
                      <div
                        key={index}
                        className="bg-gradient-to-t from-blue-500 to-purple-500 rounded-t flex-1 transition-all duration-1000"
                        style={{ height: `${height}%` }}
                      ></div>
                    ))}
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">24.8K</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Clicks</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">89.2%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">CTR</div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full opacity-20 dark:opacity-10"
                animate={floatAnimation}
              />
              <motion.div
                className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full opacity-20 dark:opacity-10"
                animate={{ ...floatAnimation, transition: { ...floatAnimation.transition, delay: 3 } }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-12 text-center overflow-hidden"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.1)_0%,transparent_50%),radial-gradient(circle_at_80%_80%,rgba(255,255,255,0.1)_0%,transparent_50%)]"></div>

            <div className="relative z-10">
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                Ready to Transform Your Links?
              </h2>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Join Stackly to optimize your digital presence and drive better results.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button onClick={handleSignupNavigation} className="bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg flex items-center gap-2 text-lg">
                  Get Started Free
                  <FiArrowRight className="w-5 h-5" />
                </button>
              </div>

              <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-blue-100 text-sm">
                <div className="flex items-center gap-2">
                  <FiCheck className="w-4 h-4" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <FiCheck className="w-4 h-4" />
                  <span>7-day free trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <FiCheck className="w-4 h-4" />
                  <span>Cancel anytime</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;